/**
 * Post-login redirect helper.
 *
 * The WorkOS round-trip from /login → workos.com → /auth/callback loses
 * React Router's location.state. To preserve a deep-link target across
 * the round-trip we stash it in sessionStorage before kicking off the
 * sign-in, then read it back in the callback.
 *
 * sessionStorage scope is per-tab — the value survives the WorkOS
 * redirect (same tab) but doesn't leak across tabs or persist after
 * the browser closes.
 *
 * Helpers also enforce a same-origin policy: only relative paths
 * starting with `/` are accepted, with a small set of disallowed
 * prefixes (e.g., `//foo.com` which browsers treat as protocol-relative
 * and would redirect off-site). This is defense against an attacker
 * crafting an email link that bounces a user to a phishing page after
 * legit login.
 */
const POST_LOGIN_REDIRECT_KEY = 'esig-post-login-redirect';

export { POST_LOGIN_REDIRECT_KEY };

/**
 * Returns true if the path is safe to redirect to after login —
 * a relative path on this origin, not a protocol-relative URL or
 * an absolute URL on another origin.
 */
export function isSafeRedirectTarget(target: string | null | undefined): boolean {
  if (!target || typeof target !== 'string') return false;
  // Must be a relative path starting with exactly one /
  if (!target.startsWith('/')) return false;
  // Reject protocol-relative URLs (//evil.com/...) and any //
  if (target.startsWith('//')) return false;
  // Reject /\ which some browsers/parsers treat as protocol-relative
  if (target.startsWith('/\\')) return false;
  return true;
}

/**
 * Stash a target URL in sessionStorage before redirecting to WorkOS.
 * Validates the target is safe; silently no-ops if not.
 */
export function stashPostLoginRedirect(target: string | null | undefined): void {
  if (!isSafeRedirectTarget(target)) return;
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, target as string);
  } catch {
    // sessionStorage unavailable (private mode, quota, etc.) — fall
    // through; user will land on role-default route after login.
  }
}

/**
 * Read and clear the stashed post-login redirect.
 *
 * Returns the target if present and safe, otherwise null. Always
 * clears storage on read so a stale value doesn't survive multiple
 * login attempts in the same tab.
 */
export function consumePostLoginRedirect(): string | null {
  try {
    const target = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return isSafeRedirectTarget(target) ? target : null;
  } catch {
    return null;
  }
}
